import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import TitleDescription from "../../../../components/newMotorPolicy/titleDescription/titleDescription";
import PolicyCard from "../../../../components/policyCard/policyCard";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import usePolicyCancellationFlow from "../../../../hooks/usePolicyCancellationFlow";
import { selectProfileData } from "../../../../redux/features/profileSlice";
import {
  fetchOccupationsList,
  selectOccupations,
} from "../../../../redux/features/constants/occupationsListSlice";
import { getClientSource } from "../../../../utils/getClientSource";
import { formatPremiumWithCurrency } from "../../../../utils/index";
import { generatePolicyCardInfo } from "../../../../utils/policyCardUtils";
import BottomLayout from "../../../motorInsurancePages/policyOverview/managePolicy/policyCancellationFlow/bottomLayout";
import PolicyCancellationMotorLayout from "../../../motorInsurancePages/policyOverview/managePolicy/policyCancellationFlow/policyCancellationMotorLayout";
import AlertComponent from "../../../motorInsurancePages/transferPolicyFlow/alertComponent";

const PersonalAccidentReviewCancellationPage = ({
  handleNavigationToPolicyOverview,
}) => {
  const translate = useLanguageTranslate();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { handleNextSubstep } = usePolicyCancellationFlow();
  const { i18n, t } = useTranslation();

  // Always use English numerals, detect RTL for currency positioning
  const isRTL = i18n.resolvedLanguage === "ar";

  // Get policy detail from Redux (same selector as travel uses)
  const policyDetailsInfo = useSelector(
    (state) => state.policyDetails?.PolicyDetails?.PolicyHeader
  );
  // Get logged in user profile info
  const profileInfo = useSelector(selectProfileData);

  // Occupations list to convert the occupation code returned by the API into a name
  const occupationsData = useSelector(selectOccupations);
  useEffect(() => {
    if (!occupationsData || occupationsData.length === 0) {
      dispatch(fetchOccupationsList());
    }
  }, [dispatch, occupationsData]);
  const getOccupationName = (occupationCode) => {
    if (!occupationCode) return translate("N/A", "غير متوفر");
    const occupation = occupationsData?.find(
      (occ) => occ.Code.toString() === occupationCode.toString()
    );
    if (occupation) {
      return translate(occupation.Name, occupation.NameAr);
    }
    return translate("N/A", "غير متوفر");
  };

  // DETAILED LOGGING FOR BACKEND TEAM
  console.log("=== PERSONAL ACCIDENT POLICY CANCEL DATA DEBUG ===");
  console.log("Full policyDetailsInfo no json:", policyDetailsInfo);
  console.log(
    "Full policyDetailsInfo:",
    JSON.stringify(policyDetailsInfo, null, 2)
  );
  console.log("FromDateStr:", policyDetailsInfo?.FromDateStr);
  console.log("ToDateStr:", policyDetailsInfo?.ToDateStr);
  console.log("PolicyNumber:", policyDetailsInfo?.PolicyNumber);
  console.log("PlanName:", policyDetailsInfo?.PlanName);
  console.log("ProductCode:", policyDetailsInfo?.ProductCode);
  console.log("Premium:", policyDetailsInfo?.Premium);
  console.log("=== END DEBUG ===");

  // Helper function for RTL-aware currency formatting - always use English numerals
  const formatCurrency = (amount) => {
    const currency = translate("BHD", "د.ب");
    const formattedNumber = formatPremiumWithCurrency(amount, "", "en-US");

    // For RTL, put number first then currency (66.000 د.ب)
    if (isRTL) {
      return (
        <span className="flex items-center gap-1 flex-row-reverse rtl:justify-end">
          <span>{currency}</span>
          <span>{formattedNumber}</span>
        </span>
      );
    }
    // For LTR, currency before number
    return `${currency} ${formattedNumber}`;
  };

  // Helper to format DOB as DD/MM/YYYY
  const formatDOB = (dateStr) => {
    if (!dateStr) return translate("N/A", "غير متوفر");
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-GB"); // DD/MM/YYYY
    } catch {
      return dateStr;
    }
  };

  // Helper function to format date to English format (10 July 2025)
  const formatDateToEnglish = (dateStr) => {
    if (!dateStr) return "--";

    try {
      const parts = dateStr.split("/");
      const date = new Date(parts[2], parts[1] - 1, parts[0]);
      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  // Generate policy card info using utility function
  const policyCardInfo = generatePolicyCardInfo(
    policyDetailsInfo,
    getClientSource()
  );

  /* Next */
  const continueClickHandler = () => {
    handleNextSubstep();
  };

  return (
    <PolicyCancellationMotorLayout
      is_hide_back_btn={false}
      show_close_btn={true}
      handleCloseBtnClick={handleNavigationToPolicyOverview}
      btn_onClick={continueClickHandler}
      btn_disabled={false}
      btn_label={translate("Continue", "التالي")}
      CancellationAdditionalComponent={
        <BottomLayout
          className="btvk"
          btn_onClick={continueClickHandler}
          btn_disabled={false}
          btn_label={translate("Continue", "التالي")}
          handleNavigationToPolicyOverview={handleNavigationToPolicyOverview}
        />
      }
    >
      <div className="flex flex-col items-center mx-auto max-w-lg pb-20 max767:p-5 max767:mt-0 max767:pt-0 max767:pb-[50px]">
        <div className="flex justify-center items-center w-full">
          <div className=" p-0 w-full ">
            <TitleDescription
              className="max767:mt-0!important"
              title={translate("Policy Cancellation", "تسليم سياستك")}
              description={translate(
                "Please review your personal accident policy details before proceeding with cancellation.",
                "قم بمراجعة تفاصيل سياستك وقم بتوفير المعلومات اللازمة للمضي قدمًا في عملية الاستسلام."
              )}
            />
            <AlertComponent
              className="mb-4"
              title={translate(
                "Note that cancelling your policy is a request and is subject to approval based on our cancellation policy. Ensure you understand the implications of this action.",
                "يرجى ملاحظة أن هذا الطلب سيتم تقديمه للموافقة عليه قبل أن يصبح ساري المفعول"
              )}
            />
            <div className="flex flex-col justify-center items-center mt-5 bg-[#F6F8F9] p-4 rounded-2xl max767:bg-white max767:p-0">
              <PolicyCard
                className="mb-4"
                policy_name="Personal Accident Insurance"
                policy_label={profileInfo?.CPR || policyDetailsInfo?.CPR || "N/A"}
                policy_number={policyDetailsInfo?.PolicyNumber || "N/A"}
                from_date={policyCardInfo.FromDateStr}
                to_date={policyCardInfo.ToDateStr}
                policy_bg_image={policyCardInfo.policy_bg_image}
                expire_days={policyCardInfo.expire_days}
                is_text_dark={policyCardInfo.is_text_dark}
                mapping_key={policyCardInfo.id}
                data={policyCardInfo}
                disableCardClick
              />

              {/* Personal Accident specific table - only 3 fields */}
              {isRTL ? (
                /* Arabic: Use CSS Grid for equal columns */
                <div className="w-full bg-white border border-solid border-[#DDE2E4] rounded-[10px] overflow-hidden">
                  {/* 1. Full Name */}
                  <div className="grid grid-cols-2 border-b border-[#DDE2E4]">
                    <div className="px-4 border-l border-[#DDE2E4] text-[#5B6871] text-sm font-medium h-[44px] leading-[44px]">
                      {translate("Full Name", "الاسم الكامل")}
                    </div>
                    <div className="py-2 px-4 text-[#1A2024] text-sm font-medium leading-6">
                      {profileInfo?.FirstName && profileInfo?.LastName
                        ? `${profileInfo.FirstName} ${profileInfo.LastName}`
                        : translate("N/A", "غير متوفر")}
                    </div>
                  </div>

                  {/* 2. CPR */}
                  <div className="grid grid-cols-2 border-b border-[#DDE2E4]">
                    <div className="px-4 border-l border-[#DDE2E4] text-[#5B6871] text-sm font-medium h-[44px] leading-[44px]">
                      {translate("CPR Number", "رقم الهوية الوطنية")}
                    </div>
                    <div className="py-2 px-4 text-[#1A2024] text-sm font-medium leading-6">
                      {profileInfo?.CPR || translate("N/A", "غير متوفر")}
                    </div>
                  </div>

                  {/* 3. Occupation */}
                  <div className="grid grid-cols-2 border-b border-[#DDE2E4]">
                    <div className="px-4 border-l border-[#DDE2E4] text-[#5B6871] text-sm font-medium h-[44px] leading-[44px]">
                      {translate("Occupation", "المهنة")}
                    </div>
                    <div className="py-2 px-4 text-[#1A2024] text-sm font-medium leading-6">
                      {getOccupationName(policyDetailsInfo?.Occupation)}
                    </div>
                  </div>

                  {/* 4. Date of Birth */}
                  <div className="grid grid-cols-2 border-b border-[#DDE2E4]">
                    <div className="px-4 border-l border-[#DDE2E4] text-[#5B6871] text-sm font-medium h-[44px] leading-[44px]">
                      {translate("Date of Birth", "تاريخ الميلاد")}
                    </div>
                    <div className="py-2 px-4 text-[#1A2024] text-sm font-medium leading-6">
                      {formatDOB(profileInfo?.DateOfBirth || profileInfo?.DOB)}
                    </div>
                  </div>

                  {/* 5. Sum Insured Limit */}
                  <div className="grid grid-cols-2">
                    <div className="px-4 border-l border-[#DDE2E4] text-[#5B6871] text-sm font-medium h-[44px] leading-[44px]">
                      {translate("Sum Insured Limit", "مبلغ التأمين")}
                    </div>
                    <div className="py-2 px-4 text-[#1A2024] text-sm font-medium leading-6">
                      {formatCurrency(policyDetailsInfo?.SumInsured)}
                    </div>
                  </div>
                </div>
              ) : (
                /* English: Use table */
                <table className="min-w-full border-separate bg-white border border-solid border-[#DDE2E4] border-spacing-0 rounded-[10px]">
                  <tbody>
                    {/* 1. Full Name - from logged in user */}
                    <tr>
                      <td className="h-[44px] leading-[44px] py-0 px-4 border-b border-r text-[#5B6871] text-sm font-medium leading-6">
                        {translate("Full Name", "الاسم الكامل")}
                      </td>
                      <td className="py-0 px-4 border-b text-[#1A2024] text-sm font-medium leading-6">
                        {profileInfo?.FirstName && profileInfo?.LastName
                          ? `${profileInfo.FirstName} ${profileInfo.LastName}`
                          : translate("N/A", "غير متوفر")}
                      </td>
                    </tr>

                    {/* 2. CPR - from logged in user */}
                    <tr>
                      <td className="h-[44px] leading-[44px] py-0 px-4 border-b border-r text-[#5B6871] text-sm font-medium leading-6">
                        {translate("CPR Number", "رقم الهوية الوطنية")}
                      </td>
                      <td className="py-0 px-4 border-b text-[#1A2024] text-sm font-medium leading-6">
                        {profileInfo?.CPR || translate("N/A", "غير متوفر")}
                      </td>
                    </tr>

                    {/* 3. Occupation */}
                    <tr>
                      <td className="h-[44px] leading-[44px] py-0 px-4 border-b border-r text-[#5B6871] text-sm font-medium leading-6">
                        {translate("Occupation", "المهنة")}
                      </td>
                      <td className="py-0 px-4 border-b text-[#1A2024] text-sm font-medium leading-6">
                        {getOccupationName(policyDetailsInfo?.Occupation)}
                      </td>
                    </tr>

                    {/* 4. Date of Birth */}
                    <tr>
                      <td className="h-[44px] leading-[44px] py-0 px-4 border-b border-r text-[#5B6871] text-sm font-medium leading-6">
                        {translate("Date of Birth", "تاريخ الميلاد")}
                      </td>
                      <td className="py-0 px-4 border-b text-[#1A2024] text-sm font-medium leading-6">
                        {formatDOB(profileInfo?.DateOfBirth || profileInfo?.DOB)}
                      </td>
                    </tr>

                    {/* 5. Sum Insured Limit - with proper formatting */}
                    <tr>
                      <td className="h-[44px] leading-[44px] py-0 px-4 border-b border-r text-[#5B6871] text-sm font-medium leading-6">
                        {translate("Sum Insured Limit", "مبلغ التأمين")}
                      </td>
                      <td className="py-0 px-4 border-b text-[#1A2024] text-sm font-medium leading-6">
                        {formatCurrency(policyDetailsInfo?.SumInsured)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </PolicyCancellationMotorLayout>
  );
};

export default PersonalAccidentReviewCancellationPage;
